import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Copy, Loader2, MailCheck, XCircle } from "lucide-react";
import { API } from "@/config/site";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StatusTracker from "@/components/StatusTracker";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const store = useStore();
  const { clearCart, setBuyNow } = useCart();
  const [phase, setPhase] = useState("checking");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) {
      setPhase("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${orderId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          const res = await axios.get(`${API}/orders/by-session/${orderId}`);
          if (cancelled) return;
          setOrder(res.data);
          setPhase("paid");
          const src = localStorage.getItem("vault_pending_source");
          if (src === "cart") clearCart();
          setBuyNow(null);
          localStorage.removeItem("vault_pending_source");
          return;
        }
        tries.current += 1;
        if (tries.current < 40) setTimeout(poll, 2500);
        else setPhase("error");
      } catch {
        if (!cancelled) {
          tries.current += 1;
          if (tries.current < 40) setTimeout(poll, 2500);
          else setPhase("error");
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success("Code copied");
  };

  const refreshOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/by-session/${orderId}`);
      if (res.data?.payment_status === "paid") {
        setOrder(res.data);
        toast.success("Status refreshed");
      }
    } catch {}
  };

  if (phase === "checking") {
    return (
      <div className="vault-container flex flex-col items-center pt-32 text-center" data-testid="confirmation-processing">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">
          Confirming your payment…
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Payment confirmed. We're preparing your digital code.
        </p>
      </div>
    );
  }

  if (phase === "error" || !order) {
    return (
      <div className="vault-container flex flex-col items-center pt-32 text-center" data-testid="confirmation-error">
        <XCircle className="h-8 w-8 text-red-400" />
        <h1 className="mt-6 font-display text-2xl font-semibold tracking-tight">
          We couldn't confirm this order
        </h1>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">
          If you completed payment, your code is on its way — check your email, or contact support
          and we'll sort it out.
        </p>
        <Link to="/cards" className="mt-8 rounded-full border border-white/15 px-6 py-3 text-sm text-muted-foreground hover:text-foreground">
          Back to the store
        </Link>
      </div>
    );
  }

  return (
    <div className="vault-container max-w-3xl pt-16" data-testid="order-confirmation-page">
      <div className="flex flex-col items-center text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
          <CheckCircle2 className="h-7 w-7 text-primary" />
        </span>
        <p className="overline-label mt-6">Order {order.order_id}</p>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tighter sm:text-4xl">
          Payment confirmed.
        </h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground" data-testid="confirmation-delivery-notice">
          {order.delivery_status === "code_delivered" ? (
            <>Your code has been sent to <span className="text-foreground">{order.email}</span>.</>
          ) : (
            <>
              We're verifying your payment. Your code will be emailed to{" "}
              <span className="text-foreground">{order.email}</span> after verification — usually
              within a few hours.
            </>
          )}
        </p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-[1fr_240px]">
        <div className="rounded-2xl border border-white/[0.08] bg-card p-6">
          <p className="overline-label mb-4">Delivery status</p>
          <StatusTracker status={order.delivery_status} history={order.status_history} />
          {order.delivery_status !== "code_delivered" && (
            <button
              data-testid="refresh-status-button"
              onClick={refreshOrder}
              className="mt-4 rounded-full border border-white/15 px-4 py-2 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
            >
              Refresh status
            </button>
          )}
        </div>
        <div className="flex flex-col justify-center gap-2 rounded-2xl border border-primary/25 bg-primary/[0.06] p-6 text-xs text-primary">
          <MailCheck className="h-5 w-5" />
          <p className="font-medium">Digital delivery by email</p>
          <p className="text-primary/70">No shipping, no waiting rooms.</p>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="confirmation-items">
        <p className="overline-label mb-5">Your order</p>
        <div className="flex flex-col gap-5">
          {order.items.map((it, idx) => (
            <div key={idx} className="flex gap-4" data-testid={`confirmation-item-${idx}`}>
              <img
                src={it.image}
                alt={`${it.name} artwork`}
                className="aspect-[1.586/1] w-28 shrink-0 rounded-lg border border-white/[0.08] object-cover"
              />
              <div className="flex flex-1 items-start justify-between gap-3 text-sm">
                <div>
                  <p className="font-medium">{it.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {it.unit_price} {order.currency} × {it.quantity}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground/80">
                    Redeemable in: {(it.redeemable_names || []).join(", ")}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <div className="flex justify-between border-t border-white/[0.08] pt-4 text-sm">
            <span className="text-muted-foreground">Total paid</span>
            <span className="font-mono font-semibold" data-testid="confirmation-total">
              {order.total} {order.currency}
            </span>
          </div>
        </div>
      </div>

      {order.codes?.length > 0 && (
      <div className="mt-6 rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="confirmation-codes">
        <p className="overline-label mb-5">Your codes</p>
        <div className="flex flex-col gap-3">
          {order.codes.map((c, idx) => (
            <div
              key={idx}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3.5"
              data-testid={`confirmation-code-${idx}`}
            >
              <div>
                <p className="text-xs text-muted-foreground">
                  {c.name} · {c.label} · {c.region_name}
                </p>
                <p className="mt-1 font-mono text-sm font-semibold tracking-wider">{c.code}</p>
              </div>
              <button
                data-testid={`copy-code-${idx}`}
                onClick={() => copyCode(c.code)}
                className="flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link
          to="/cards"
          data-testid="confirmation-continue-shopping"
          className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:scale-[1.03]"
        >
          Continue shopping
        </Link>
        <Link
          to="/account/orders"
          data-testid="confirmation-view-orders"
          className="rounded-full border border-white/15 px-6 py-3 text-sm text-muted-foreground transition-colors duration-200 hover:border-white/30 hover:text-foreground"
        >
          View my orders
        </Link>
      </div>
    </div>
  );
}
